'use client';

/**
 * ResponsiveModal Component
 *
 * A responsive modal that adapts to mobile and desktop layouts.
 * Uses Dialog on desktop and Drawer on mobile for optimal UX.
 */
import React from 'react';

import { Dialog } from '../../../common/dialog/basicDialog';
import { Drawer } from '../../../common/drawer';

export interface ResponsiveModalProps {
  /**
   * Whether the modal is open
   */
  isOpen: boolean;
  /**
   * Callback when modal should close
   */
  onClose: () => void;
  /**
   * Content to display inside the modal
   */
  children: React.ReactNode;
  /**
   * Title for the modal (used primarily on mobile)
   */
  title?: string;
  /**
   * Whether this should render as mobile layout
   * If not provided, will use a default responsive behavior
   */
  forceMobile?: boolean;
  /**
   * Whether this should render as desktop layout
   * If not provided, will use a default responsive behavior
   */
  forceDesktop?: boolean;
  /**
   * Custom CSS class for the modal content
   */
  className?: string;
  /**
   * Whether to show close button on desktop dialog
   * @default false
   */
  showCloseButton?: boolean;
  /**
   * Whether to show handle on mobile drawer
   * @default true
   */
  showHandle?: boolean;
  /**
   * Drawer side for mobile
   * @default 'bottom'
   */
  drawerSide?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Custom styling for desktop dialog
   */
  dialogClassName?: string;
  /**
   * Custom styling for mobile drawer
   */
  drawerClassName?: string;
}

/**
 * Hook to detect mobile viewport with stabilization
 * Falls back to CSS-based responsive behavior if not available
 */
const useIsMobile = () => {
  const [isMobile, setIsMobile] = React.useState(false);
  const [mounted, setMounted] = React.useState(false);

  React.useEffect(() => {
    setMounted(true);

    const checkMobile = () => {
      if (typeof window !== 'undefined') {
        setIsMobile(window.innerWidth < 768); // md breakpoint
      }
    };

    checkMobile();

    // Add debounced resize listener to prevent rapid toggling
    let timeoutId: NodeJS.Timeout;
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(checkMobile, 150);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, []);

  // Return false during SSR and initial client-side render to prevent hydration mismatch
  if (!mounted) return false;

  return isMobile;
};

/**
 * ResponsiveModal component that adapts to screen size
 *
 * @example
 * ```tsx
 * // Basic usage
 * <ResponsiveModal
 *   isOpen={isModalOpen}
 *   onClose={() => setIsModalOpen(false)}
 *   title="Select Options"
 * >
 *   <QuantitySelector {...quantityProps} />
 * </ResponsiveModal>
 *
 * // Force mobile layout
 * <ResponsiveModal
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   title="Mobile View"
 *   forceMobile={true}
 * >
 *   {content}
 * </ResponsiveModal>
 *
 * // Custom styling
 * <ResponsiveModal
 *   isOpen={isOpen}
 *   onClose={onClose}
 *   title="Custom Modal"
 *   dialogClassName="max-w-md"
 *   drawerClassName="h-96"
 * >
 *   {content}
 * </ResponsiveModal>
 * ```
 */
export const ResponsiveModal = ({
  isOpen,
  onClose,
  children,
  title,
  forceMobile,
  forceDesktop,
  className = 'flex flex-col gap-3 px-5 py-4',
  showCloseButton = false,
  showHandle = true,
  drawerSide = 'bottom',
  dialogClassName,
  drawerClassName = 'p-0 rounded-t-xl'
}: ResponsiveModalProps) => {
  const isMobile = useIsMobile();

  // Determine which layout to use
  const shouldUseMobile = forceMobile || (!forceDesktop && isMobile);

  if (shouldUseMobile) {
    // Mobile: Use Drawer
    return (
      <Drawer
        key="responsive-modal-drawer"
        open={isOpen}
        onOpenChange={onClose}
        side={drawerSide}
        title={title}
        trigger={<div style={{ display: 'none' }} />}
        className={drawerClassName}
        showHandle={showHandle}
      >
        <div className={className}>{children}</div>
      </Drawer>
    );
  } else {
    // Desktop: Use Dialog
    return (
      <Dialog
        key="responsive-modal-dialog"
        isOpen={isOpen}
        onClose={onClose}
        className={dialogClassName || className}
        showCloseButton={showCloseButton}
      >
        {children}
      </Dialog>
    );
  }
};
